import { useLayoutEffect, useRef, useState } from 'react';
import { Camera, KeyRound, LifeBuoy } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import Motivo from '../experiencia/Motivo';
import EscanerCamara from '../mobile/EscanerCamara';
import { leerErrorDelContrato } from './errorDelContrato';
import { formatearCodigo } from './codigoDeTerminal';
import {
  AVISO_PANTALLA, LARGO_ACTIVACION, avisoDePegado, avisoDeProhibidas, comoElServidor, cursorTrasFormatear,
  leerCodigoDeTarjeta, leerTarjeta, significativosAntes,
} from './codigoDeActivacion';

/* EL CÓDIGO DE ACTIVACIÓN DE LA CAJA (DISEÑO-F1c §7). Uno para las tres vías
   de «Agregar terminal»: aprobar con el código de la pantalla, «Darme un
   número» y Bluetooth. Activa con POST /terminales/activar y avisa con
   `onActivada({serie})`; la vía sigue con su paso de siempre. */
/* Con `onCodigo` la sección NO lleva botón: entrega el código completo (o
   '' mientras falte) y quien la usa activa en su propio botón principal,
   en una sola pulsación («Activar y aprobar»). */
/* TERMINAL-CODIGO-TARJETA (11 oct 2026), lo que el dueño encontró en su
   Android con Gboard:
   · `codigoDePantalla`: el código corto de la terminal ya elegida. Si se
     escribe aquí (o cualquier otro con forma 3-3), se dice cuál va.
   · `onOtraTerminal(código)`: si se escanea o escribe el código de OTRA
     terminal, se lleva a emparejar ésa en vez de decir «no es una tarjeta».
   · el campo no se reformatea mientras el teclado compone (Gboard), y el
     cursor se queda donde estaba al meter el guion.
   · «No tengo tarjeta» dice de dónde sale el código y abre un ticket
     «Problema con TEGO» con la serie (la ruta de tickets que ya existe).
   Segunda vuelta (AUDITORIA-CODIGO-TARJETA): el guion tecleado tras 3 se
   conserva y con 3-3 se avisa en vivo; lo pegado con varios códigos no se
   adivina; el QR de otra terminal pregunta antes de cambiar; lleno, no se
   pierde nada; un ticket por pantalla. */
const VACIO = leerCodigoDeTarjeta('');
const LLENO = `El código tiene ${LARGO_ACTIVACION} caracteres: borra uno antes de escribir otro.`;
const DE_UN_GOLPE = /^insert(FromPaste|FromDrop|ReplacementText|FromYank|FromPasteAsQuotation)$/;

export default function CodigoDeActivacion({
  serie, onActivada, onCodigo, textoBoton = 'Activar', codigoDePantalla = null, onOtraTerminal = null,
}) {
  const [leido, setLeido] = useState(VACIO);
  const [escaneando, setEscaneando] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [sinTarjeta, setSinTarjeta] = useState(false);
  const [folio, setFolio] = useState(null);      // un ticket por pantalla (P2-5)
  const [cambio, setCambio] = useState(null);    // { codigo, serie } por confirmar (P1-3)
  const [nota, setNota] = useState(null);
  const campo = useRef(null);
  const componiendo = useRef(false);
  const cursor = useRef(null);
  const { codigo, mostrado, prohibidas, pantalla, completo } = leido;

  // El cursor detrás del mismo carácter que antes de meter o quitar el guion.
  useLayoutEffect(() => {
    const el = campo.current;
    if (cursor.current != null && el && typeof document !== 'undefined' && document.activeElement === el) {
      try { el.setSelectionRange(cursor.current, cursor.current); } catch { /* campo sin selección */ }
    }
    cursor.current = null;
  });

  function aplicar(r) {
    setLeido(r);
    setError(null); setAviso(null); setNota(null); setCambio(null);
    onCodigo?.(r.completo ? r.codigo : '');
    if (r.otraSerie) setAviso(`Esa tarjeta es de la terminal ${r.otraSerie}. Escanea la de esta caja.`);
  }

  function procesar(valor, pos, tipo) {
    // Lo que había antes se cuenta por lo que se VEÍA, no por el código: en
    // el estado «pantalla» el código está vacío y cualquier retoque parecía
    // un pegado (P2-2).
    const antes = significativosAntes(leido.mostrado, leido.mostrado.length);
    const ahora = comoElServidor(valor).length;
    const deUnGolpe = DE_UN_GOLPE.test(tipo || '') || ahora - antes >= 4;
    // Lleno y tecleando: no se pierde el último en silencio (P2-3).
    if (!deUnGolpe && ahora > LARGO_ACTIVACION && /^[A-Za-z0-9\s-]*$/.test(valor)) {
      cursor.current = Math.max(0, (pos ?? valor.length) - 1);
      setLeido({ ...leido });
      setNota(LLENO);
      return;
    }
    let n = significativosAntes(valor, pos ?? valor.length);
    let r = leerCodigoDeTarjeta(valor, { codigoDePantalla, deUnGolpe, serie });
    // Borrar justo detrás del guion que pone la máscara (el del 5-5) se
    // lleva el carácter de antes: si no, el guion vuelve y la tecla de
    // borrar parece no hacer nada. El guion tecleado tras 3 se borra solo.
    if (tipo === 'deleteContentBackward' && pos === 5 && leido.mostrado.charAt(5) === '-'
        && r.codigo === leido.codigo && n > 0) {
      r = leerCodigoDeTarjeta(r.codigo.slice(0, n - 1) + r.codigo.slice(n), { codigoDePantalla, serie });
      n -= 1;
    }
    let c = cursorTrasFormatear(r.mostrado, n);
    // Recién tecleado el guion (o un espacio) y conservado: el cursor va detrás.
    if (/[\s-]/.test(valor.charAt((pos ?? valor.length) - 1)) && r.mostrado.charAt(c) === '-') c += 1;
    cursor.current = deUnGolpe ? r.mostrado.length : c;
    aplicar(r);
  }

  function cambiar(e) {
    const el = e.target;
    const tipo = e.nativeEvent?.inputType || '';
    if (componiendo.current || e.nativeEvent?.isComposing) {
      // Gboard está componiendo la palabra: lo que se ve no se toca hasta
      // que termine, o el teclado duplica lo escrito.
      const r = leerCodigoDeTarjeta(el.value, { codigoDePantalla });
      setLeido({ ...r, mostrado: el.value });
      onCodigo?.(r.completo ? r.codigo : '');
      return;
    }
    procesar(el.value, el.selectionStart, tipo);
  }

  function terminarComposicion(e) {
    componiendo.current = false;
    procesar(e.target.value, e.target.selectionStart, 'insertCompositionText');
  }

  // Al salir del campo NO se acusa: 6 caracteres sin el guion 3-3 pueden
  // ser una tarjeta a medias (P2-1). Lo que tiene forma de pantalla ya se
  // avisó mientras se escribía.

  function teclaEnter(e) {
    if (e.key === 'Enter' && !onCodigo) {
      e.preventDefault();
      if (completo && !trabajando) activar();
    }
  }

  // Lo que se reconoce como QR o código de UNA TERMINAL, no de su tarjeta.
  function esDeTerminal(r) {
    if (r.enlace && r.enlace !== 'VINCULAR') {
      setAviso('Ese es el QR de turno de una terminal que ya trabaja. Aquí va el código de 10 caracteres '
        + 'de la tarjeta de la caja.');
      return;
    }
    const cod = r.pantalla;
    if (codigoDePantalla && cod === comoElServidor(codigoDePantalla)) {
      setAviso(`Ese QR es el de la pantalla de esta misma terminal (${formatearCodigo(cod)}), que ya está elegida. `
        + 'Aquí falta el código de 10 caracteres de la tarjeta de su caja.');
      return;
    }
    if (onOtraTerminal) { preguntarCambio(cod); return; }
    setAviso(AVISO_PANTALLA);
  }

  // P1-3: el QR de OTRA terminal no cambia de terminal sin preguntar. Se
  // busca su serie para nombrarla; si no se encuentra, se nombra el código.
  function preguntarCambio(cod) {
    setCambio({ codigo: cod, serie: null });
    api.get(`/terminal/v2/vincular/${cod}`)
      .then((d) => setCambio((c) => (c && c.codigo === cod ? { ...c, serie: d?.serie || null } : c)))
      .catch(() => {});
  }

  function escaneado(texto) {
    setEscaneando(false);
    const t = leerTarjeta(texto);
    if (t) {
      if (serie && t.serie !== serie) {
        setAviso(`Esa tarjeta es de la terminal ${t.serie}. Escanea la de esta caja.`);
        return;
      }
      aplicar(leerCodigoDeTarjeta(t.codigo));
      return;
    }
    const r = leerCodigoDeTarjeta(texto, { codigoDePantalla, deUnGolpe: true });
    if (r.pantalla) { setLeido(VACIO); onCodigo?.(''); esDeTerminal(r); return; }
    if (r.completo) { aplicar(r); return; }
    setAviso('Lo que leyó la cámara no es una tarjeta de activación de TEGO. Escanea el QR de la tarjeta de la caja.');
  }

  async function activar() {
    setTrabajando(true); setError(null);
    try {
      const d = await api.post('/terminales/activar', { serie, codigo });
      onActivada?.(d);
    } catch (err) {
      setError(leerErrorDelContrato(err, { terminal: 'esta terminal' }));
    } finally {
      setTrabajando(false);
    }
  }

  const motivo = completo ? null
    : leido.pegado ? 'Pega solo el código de 10 caracteres de la tarjeta.'
    : pantalla ? 'Escribe el código de 10 caracteres de la tarjeta, no el de la pantalla.'
    : prohibidas.length ? 'Corrige el carácter marcado: la tarjeta no lo usa.'
    : 'Escribe las 10 letras y números de la tarjeta de la caja.';
  const otraTerminal = pantalla && onOtraTerminal && !(codigoDePantalla && pantalla === comoElServidor(codigoDePantalla));
  const avisoCampo = pantalla ? AVISO_PANTALLA : (avisoDePegado(leido) || avisoDeProhibidas(prohibidas));
  const tipoAviso = pantalla ? 'pantalla' : leido.pegado ? 'pegado' : 'prohibidas';
  return (
    <section className="space-y-3 rounded-xl border border-amber-300 bg-amber-50 p-4" data-terminal="activacion">
      <h3 className="flex items-center gap-2 font-semibold text-gray-900">
        <KeyRound size={18} aria-hidden="true" /> Código de activación de la caja
      </h3>
      <p className="text-sm text-gray-700">
        Esta terminal necesita el código de activación de su caja. Está en la tarjeta que venía dentro.
      </p>
      <Ayuda id="terminal.activacion.pantalla" />
      <label className="block space-y-1">
        <span className="text-sm font-medium text-gray-800">Código de la tarjeta</span>
        <input ref={campo} value={mostrado} onChange={cambiar} onKeyDown={teclaEnter}
               onCompositionStart={() => { componiendo.current = true; }} onCompositionEnd={terminarComposicion}
               type="text" name="tego-codigo-tarjeta" inputMode="text" autoCapitalize="characters"
               autoComplete="off" autoCorrect="off" spellCheck={false} enterKeyHint={onCodigo ? 'done' : 'go'}
               data-form-type="other" data-lpignore="true" data-1p-ignore="true" data-codigo-tarjeta=""
               placeholder="XXXXX-XXXXX" aria-invalid={avisoCampo ? true : undefined}
               className="w-full min-h-[44px] min-w-0 rounded-lg border border-gray-300 px-3 font-mono text-lg tracking-widest"
               aria-describedby={avisoCampo ? 'aviso-codigo-tarjeta' : (!onCodigo && motivo ? 'motivo-activacion' : undefined)} />
        <Ayuda id="terminal.activacion.codigo" />
      </label>
      {avisoCampo && (
        <p id="aviso-codigo-tarjeta" role="status" className="text-sm font-medium text-amber-900 break-words"
           data-aviso-tarjeta={tipoAviso}>{avisoCampo}</p>
      )}
      {nota && <p role="status" className="text-sm text-amber-900" data-nota-tarjeta="">{nota}</p>}
      {cambio && (
        <div role="group" aria-label="Cambiar de terminal" data-confirmar-cambio=""
             className="space-y-2 rounded-lg border border-amber-400 bg-white p-3 text-sm text-gray-900">
          <p className="break-words">
            {`Este QR es de la terminal ${cambio.serie || formatearCodigo(cambio.codigo)}, no de la tarjeta. ¿Cambiar a esa terminal?`}
          </p>
          <button type="button" onClick={() => { const c = cambio.codigo; setCambio(null); onOtraTerminal(c); }}
                  data-accion="cambiar-de-terminal"
                  className="flex w-full min-h-[44px] items-center justify-center rounded-lg bg-blue-600 px-3 font-semibold text-white">
            Cambiar a esa terminal
          </button>
          <button type="button" onClick={() => setCambio(null)} data-accion="seguir-con-esta"
                  className="flex w-full min-h-[44px] items-center justify-center rounded-lg border border-gray-300 bg-white px-3 font-semibold">
            Seguir con esta
          </button>
        </div>
      )}
      {otraTerminal && (
        <button type="button" onClick={() => onOtraTerminal(pantalla)} data-accion="ir-a-terminal"
                className="flex w-full min-h-[44px] items-center justify-center rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold">
          {`Emparejar la terminal ${formatearCodigo(pantalla)}`}
        </button>
      )}
      <button type="button" onClick={() => setEscaneando(true)}
              className="flex w-full min-h-[44px] items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3">
        <Camera size={18} aria-hidden="true" /> Escanear la tarjeta
      </button>
      {escaneando && <EscanerCamara onCodigo={escaneado} onCerrar={() => setEscaneando(false)} />}
      {aviso && <p className="text-sm text-amber-800 break-words" role="status" data-aviso-escaner="">{aviso}</p>}
      {error && (
        <p className="text-sm text-red-700" role="alert">
          {error.mensaje} {error.queHacer}
        </p>
      )}
      {!onCodigo && <Motivo id="motivo-activacion" cuando={!!motivo && !trabajando}>{motivo}</Motivo>}
      {!onCodigo && <button type="button" onClick={activar} disabled={!completo || trabajando}
              aria-describedby={motivo ? 'motivo-activacion' : undefined}
              className="w-full min-h-[44px] rounded-lg bg-blue-600 px-4 font-semibold text-white disabled:opacity-50">
        {trabajando ? 'Comprobando la terminal con TEGO…' : textoBoton}
      </button>}
      <button type="button" onClick={() => setSinTarjeta((v) => !v)} aria-expanded={sinTarjeta}
              data-accion="no-tengo-tarjeta"
              className="flex w-full min-h-[44px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold text-gray-700 underline">
        <LifeBuoy size={16} aria-hidden="true" /> No tengo tarjeta
      </button>
      {sinTarjeta && <SinTarjeta serie={serie} folio={folio} onFolio={setFolio} />}
    </section>
  );
}

/* «NO TENGO TARJETA». El código no se puede reenviar: TEGO lo guarda solo
   como huella (nube/app/fabrica.py, `hashear`). Aquí no se promete ningún
   envío: quien compró la terminal lo recibió en la caja o en su aviso, y
   soporte puede asignar la serie a la empresa. La vía es la mesa de ayuda
   que ya existe: un ticket «Problema con TEGO» con la serie (nunca con lo
   escrito en el campo), que quien atiende manda con «Escalar a TEGO». El
   folio vive en quien la monta: cerrar y abrir el panel no abre otro. */
export function SinTarjeta({ serie, folio = null, onFolio }) {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  async function pedir() {
    if (folio !== null) return;
    setEnviando(true); setError(null);
    try {
      const t = await api.post('/tickets', {
        titulo: `Código de activación de la terminal ${serie}`,
        descripcion: `No tengo la tarjeta de la caja de la terminal ${serie}. `
          + 'Pido que soporte de TEGO asigne esta serie a nuestra empresa.',
        categoria: 'TEGO',
        contexto: { pantalla: 'Agregar terminal' },
      });
      onFolio?.(t?.folio || '');
    } catch (e) {
      if (e instanceof ErrorApi && e.status === 403) {
        setError(`No tienes permiso para abrir tickets. Pide a quien administra tu empresa que abra uno `
          + `«Problema con TEGO» con la serie ${serie}.`);
      } else {
        const m = e instanceof ErrorApi && e.message ? e.message.trim() : 'No hubo respuesta del servidor.';
        setError(`No se abrió el ticket: ${/[.!?]$/.test(m) ? m : `${m}.`} Revisa la conexión y vuelve a intentarlo, `
          + `o usa «Pedir ayuda» y escribe la serie ${serie}.`);
      }
    } finally {
      setEnviando(false);
    }
  }

  const yaPedido = folio !== null;
  return (
    <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-800" data-sin-tarjeta="">
      <p>
        El código viene en la tarjeta de la caja de la terminal. Si no la tienes, soporte de TEGO
        puede asignar la serie a tu empresa.
      </p>
      <Ayuda id="terminal.activacion.no_tengo" />
      {serie ? (
        <>
          <p className="break-words">Escribe a soporte con la serie <strong className="font-mono">{serie}</strong>.</p>
          {yaPedido && (
            <p role="status" className="rounded-lg border border-green-200 bg-green-50 p-2 text-green-900 break-words" data-ticket-tego="">
              {`Listo: se abrió el ticket ${folio} en «Problema con TEGO» con la serie ${serie}. `
                + 'Quien atiende la mesa de ayuda lo manda a TEGO con «Escalar a TEGO».'}
            </p>
          )}
          <Motivo id="motivo-ticket-tego" cuando={yaPedido}>
            {`Ya se abrió el ticket ${folio} para esta terminal: síguelo en la mesa de ayuda.`}
          </Motivo>
          <button type="button" onClick={pedir} disabled={enviando || yaPedido} aria-busy={enviando || undefined}
                  aria-describedby={yaPedido ? 'motivo-ticket-tego' : undefined} data-accion="pedir-a-soporte"
                  className="flex w-full min-h-[44px] items-center justify-center rounded-lg border border-gray-300 bg-white px-3 font-semibold disabled:opacity-50">
            {enviando ? 'Abriendo el ticket…' : 'Abrir un ticket para soporte de TEGO'}
          </button>
          {error && <p role="alert" className="text-red-700 break-words">{error}</p>}
        </>
      ) : (
        <p className="break-words">Escribe a soporte con la serie que trae la etiqueta de la terminal (empieza con TB-).</p>
      )}
    </div>
  );
}
